import { Hub, useAuth } from '@backoffice-frontend/authentication'
import { LOGIN_INTENDED_PATH, Routes } from '@backoffice-frontend/common'
import { useMediaQuery, useTheme } from '@backoffice-frontend/patterns'
import { WelcomeAreaRoutes } from '@backoffice-frontend/welcome-api'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Route,
  Routes as RouterRoutes,
  useNavigate,
  useSearchParams,
} from 'react-router'
import { ForgotPasswordView } from './ForgotPasswordView'
import { LoginView } from './LoginView'
import { SetNewPasswordView } from './SetNewPasswordView'

export const PublicAppView = ({
  title,
  description,
}: {
  title: string
  description?: string
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [ssoError, setSsoError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { isAuthenticated, loading, checkAuthState } = useAuth()

  const isProcessingSSO =
    searchParams.has('code') ||
    searchParams.has('state') ||
    searchParams.has('error')

  // Listen for SSO completion
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuthState().then(() => {
            const savedPath = localStorage.getItem(LOGIN_INTENDED_PATH)
            localStorage.removeItem(LOGIN_INTENDED_PATH)
            const redirectPath = savedPath ?? WelcomeAreaRoutes.route
            navigate(redirectPath, { replace: true })
          })
          break
        case 'signInWithRedirect_failure':
          setSsoError(
            payload.data.error?.message ?? t('loginView_ssoGenericError'),
          )
          break
      }
    })

    return unsubscribe
  }, [navigate, checkAuthState, t])

  // On initial load, we check if the user is already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated && !isProcessingSSO) {
      navigate(WelcomeAreaRoutes.route, { replace: true })
    }
  }, [loading])

  return (
    <RouterRoutes>
      <Route
        path="/"
        element={
          <LoginView
            isProcessingSSO={isProcessingSSO}
            ssoError={ssoError}
            title={title}
            description={description}
          />
        }
      />
      <Route
        path={Routes.Auth.ForgotPassword.path}
        element={<ForgotPasswordView isMobile={isMobile} />}
      />
      <Route
        path={Routes.Auth.SetNewPassword.path}
        element={<SetNewPasswordView isMobile={isMobile} />}
      />
    </RouterRoutes>
  )
}
